<script setup lang="ts">
import { Download as DownloadIcon, WifiOff as WifiOffIcon } from '@lucide/vue'
import { ref, computed, watch } from 'vue'

import BaseModal from '@/components/BaseModal.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import BaseCheckbox from '@/components/ui/BaseCheckbox.vue'
import BaseProgressBar from '@/components/ui/BaseProgressBar.vue'
import {
  DEFAULT_MAP_STYLE_PREFERENCE,
  normalizeMapStylePreference,
  type MapStylePreference,
} from '@/config/mapStyle'
import { formatByteSize } from '@/services/formattingService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { OFFLINE_MAX_ZOOM, type OfflineEstimate } from '@/services/offlineTilesService'
import { useOfflineStore } from '@/stores/offlineStore'
import { usePhotoStore } from '@/stores/photoStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'

interface Props {
  isOpen: boolean
  stylePreference: MapStylePreference
}

interface Emits {
  close: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const offlineStore = useOfflineStore()
const workspaceStore = useWorkspaceStore()
const photoStore = usePhotoStore()

const includePhotos = ref(offlineStore.getSavedIncludePhotos())
const includeTerrain = ref(offlineStore.getSavedIncludeTerrain())
const isEstimating = ref(false)
const estimate = ref<OfflineEstimate | null>(null)
const isDownloading = ref(false)
const preparationError = ref<string | null>(null)
const downloadStylePreference = ref<MapStylePreference>({ ...DEFAULT_MAP_STYLE_PREFERENCE })

const workspaceId = computed(() => workspaceStore.clientId)
const activeDownload = computed(() => offlineStore.active[workspaceId.value])

const photoEstimateBytes = computed(() => photoStore.allPhotos.length * 1_500_000)

const terrainAvailable = computed(() => (estimate.value?.terrain.tileCount ?? 0) > 0)
const mapDataBytes = computed(() => estimate.value?.mapData.estimatedBytes ?? 0)
const terrainBytes = computed(() => estimate.value?.terrain.estimatedBytes ?? 0)

const selectedTileCount = computed(
  () =>
    (estimate.value?.mapData.tileCount ?? 0) +
    (includeTerrain.value ? (estimate.value?.terrain.tileCount ?? 0) : 0),
)

const totalEstimateBytes = computed(
  () =>
    mapDataBytes.value +
    (includeTerrain.value ? terrainBytes.value : 0) +
    (includePhotos.value ? photoEstimateBytes.value : 0),
)

const showLargeWarning = computed(
  () => totalEstimateBytes.value > offlineStore.LARGE_DOWNLOAD_BYTES_THRESHOLD,
)

const progressPercent = computed(() => {
  const dl = activeDownload.value
  if (!dl || dl.overallTotal === 0) return 0
  return Math.round((dl.overallDone / dl.overallTotal) * 100)
})

const isWaiting = computed(() => activeDownload.value?.phase === 'waiting')

const progressLabel = computed(() => {
  const dl = activeDownload.value
  if (!dl) return ''
  if (dl.phase === 'waiting') return 'Waiting for another download to finish…'
  const phase = dl.phase === 'meta' ? 'Preparing' : dl.phase === 'tiles' ? 'Tiles' : 'Photos'
  return `${phase}: ${dl.overallDone} / ${dl.overallTotal}`
})

const downloadError = computed(() => activeDownload.value?.error)

watch(
  () => props.isOpen,
  async (open) => {
    if (!open) return
    downloadStylePreference.value = normalizeMapStylePreference(props.stylePreference)
    preparationError.value = null
    estimate.value = null
    isEstimating.value = true
    try {
      estimate.value = await offlineStore.estimateOfflineSize(
        workspaceStore.tours,
        workspaceStore.pois,
        downloadStylePreference.value,
      )
    } catch (error) {
      logService.warn(LOG_CATEGORIES.OFFLINE, 'Failed to estimate offline size', { error })
      preparationError.value =
        'Could not prepare the offline map. Check your connection and try again.'
    } finally {
      isEstimating.value = false
    }
  },
)

watch(activeDownload, (dl) => {
  if (dl && !dl.error) {
    isDownloading.value = true
  }
  if (!dl && isDownloading.value) {
    isDownloading.value = false
    emit('close')
  }
})

async function handleDownload() {
  if (preparationError.value) return
  isDownloading.value = true

  try {
    await offlineStore.makeOffline(
      {
        clientId: workspaceStore.clientId,
        name: workspaceStore.name,
        description: workspaceStore.description,
        isPublic: workspaceStore.isPublic ?? false,
        userRole: workspaceStore.userRole ?? null,
        canEdit: workspaceStore.canEdit ?? null,
        canView: workspaceStore.canView ?? null,
        ownerNames: workspaceStore.ownerNames ?? null,
        markedForDeletion: workspaceStore.markedForDeletion,
        deletionScheduledAt: workspaceStore.deletionScheduledAt,
      },
      workspaceStore.tours,
      photoStore.allPhotos,
      workspaceStore.pois,
      downloadStylePreference.value,
      { includePhotos: includePhotos.value, includeTerrain: includeTerrain.value },
    )
  } catch (error) {
    logService.error(LOG_CATEGORIES.OFFLINE, 'Download failed in dialog', { error })
    preparationError.value =
      'Could not start the offline download. Check your connection and try again.'
    isDownloading.value = false
  }
}

async function handleCancel() {
  if (isDownloading.value) {
    await offlineStore.cancelDownload(workspaceId.value)
    isDownloading.value = false
  }
  emit('close')
}
</script>

<template>
  <Teleport to="body">
    <BaseModal
      :is-open="isOpen"
      title="Make available offline"
      description="Download collection data for offline use"
      size="sm"
      :closeable="!isDownloading"
      show-footer
      @close="handleCancel"
    >
      <div class="space-y-4">
        <!-- Size estimate -->
        <div class="rounded-lg bg-gray-50 p-3 text-sm">
          <div v-if="preparationError" class="text-red-700" role="alert">
            {{ preparationError }}
          </div>
          <div v-else-if="isEstimating" class="py-2 text-center text-gray-500">
            Estimating download size…
          </div>
          <dl v-else class="space-y-2">
            <div class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4">
              <dt class="min-w-0 text-gray-600">Map data (z0–{{ OFFLINE_MAX_ZOOM }})</dt>
              <dd class="whitespace-nowrap text-right font-medium tabular-nums">
                {{ formatByteSize(mapDataBytes) }}
              </dd>
            </div>
            <div class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 text-gray-500">
              <dt class="min-w-0">Tile count</dt>
              <dd class="whitespace-nowrap text-right tabular-nums">
                {{ selectedTileCount.toLocaleString() }}
              </dd>
            </div>
            <div
              v-if="includeTerrain && terrainAvailable"
              class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4"
            >
              <dt class="min-w-0 text-gray-600">Terrain (hillshade &amp; contours)</dt>
              <dd class="whitespace-nowrap text-right font-medium tabular-nums">
                {{ formatByteSize(terrainBytes) }}
              </dd>
            </div>
            <div
              v-if="includePhotos"
              class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4"
            >
              <dt class="min-w-0 text-gray-600">Photos (~{{ photoStore.allPhotos.length }})</dt>
              <dd class="whitespace-nowrap text-right font-medium tabular-nums">
                {{ formatByteSize(photoEstimateBytes) }}
              </dd>
            </div>
            <div
              class="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 border-t border-gray-200 pt-2 font-medium"
            >
              <dt class="min-w-0">Estimated total</dt>
              <dd class="whitespace-nowrap text-right tabular-nums">
                {{ formatByteSize(totalEstimateBytes) }}
              </dd>
            </div>
          </dl>
        </div>

        <!-- Large download warning -->
        <div
          v-if="showLargeWarning"
          class="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800"
          role="alert"
        >
          <strong>Large download:</strong> This download requires over 1 GB. Make sure you have
          enough disk space before proceeding.
        </div>

        <!-- Terrain toggle -->
        <label v-if="terrainAvailable" class="flex items-start gap-3 hover:cursor-pointer">
          <BaseCheckbox
            v-model="includeTerrain"
            :disabled="isDownloading"
            class="mt-0.5"
            aria-describedby="terrain-hint"
          />
          <div class="min-w-0">
            <span class="text-sm font-medium text-gray-700"
              >Terrain (hillshade &amp; contours)</span
            >
            <p id="terrain-hint" class="mt-0.5 text-xs text-gray-500">
              Adds ~{{ formatByteSize(terrainBytes) }} for hillshade and contour lines.
            </p>
          </div>
        </label>

        <!-- Photos toggle -->
        <label class="flex items-start gap-3 hover:cursor-pointer">
          <BaseCheckbox
            v-model="includePhotos"
            :disabled="isDownloading"
            class="mt-0.5"
            aria-describedby="photos-hint"
          />
          <div class="min-w-0">
            <span class="text-sm font-medium text-gray-700">Include photos</span>
            <p id="photos-hint" class="mt-0.5 text-xs text-gray-500">
              Adds ~{{ formatByteSize(photoEstimateBytes) }} for {{ photoStore.allPhotos.length }}
              photos.
            </p>
          </div>
        </label>

        <!-- Progress -->
        <div v-if="isDownloading" class="space-y-2">
          <BaseProgressBar :value="progressPercent" />
          <p class="text-center text-xs text-gray-500 tabular-nums">
            {{ progressLabel }}<template v-if="!isWaiting"> ({{ progressPercent }}%)</template>
          </p>
        </div>

        <!-- Error -->
        <div
          v-if="downloadError"
          class="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
          role="alert"
        >
          <WifiOffIcon class="mr-1 inline h-4 w-4" />
          {{ downloadError }}
        </div>
      </div>

      <template #footer>
        <div class="flex w-full gap-3">
          <BaseButton variant="secondary" @click="handleCancel">
            {{ isDownloading ? 'Cancel' : 'Close' }}
          </BaseButton>
          <BaseButton
            v-if="!isDownloading"
            variant="primary"
            :disabled="isEstimating || !!preparationError"
            @click="handleDownload"
          >
            <DownloadIcon class="mr-1.5 h-4 w-4" />
            Download
          </BaseButton>
        </div>
      </template>
    </BaseModal>
  </Teleport>
</template>
